'use client'

import React, { useState, useEffect, useCallback } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { useGlobalContext } from '../context/provider'
import { AxiosGetData } from '../context/axiosfunction'
import { imageLoader } from '@/lib/functionloader'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/th'

dayjs.extend(relativeTime)
dayjs.locale('th')

// ─────────────────────────────────────────────────────────
// Countdown component
// ─────────────────────────────────────────────────────────
function Countdown({ endAt }) {
  const [timeLeft, setTimeLeft] = useState('')

  useEffect(() => {
    const calc = () => {
      const diff = dayjs(endAt).diff(dayjs())
      if (diff <= 0) { setTimeLeft('สิ้นสุดแล้ว'); return }
      const h = Math.floor(diff / 3600000)
      const m = Math.floor((diff % 3600000) / 60000)
      const s = Math.floor((diff % 60000) / 1000)
      if (h >= 24) setTimeLeft(`${Math.floor(h / 24)} วัน`)
      else setTimeLeft(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`)
    }
    calc()
    const timer = setInterval(calc, 1000)
    return () => clearInterval(timer)
  }, [endAt])

  return <span className="font-bold text-special">{timeLeft}</span>
}

// ─────────────────────────────────────────────────────────
// Badge component
// ─────────────────────────────────────────────────────────
function GrantBadge({ mode, days }) {
  if (mode === 'rolling_days') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full btn-secondary text-xs px-2.5 py-0.5">
        <svg className="w-3 h-3 text-special" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" /></svg>
        {days} วัน นับจากรับ
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full btn-secondary text-xs px-2.5 py-0.5">
      <svg className="w-3 h-3 text-info" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" /></svg>
      วันกำหนด
    </span>
  )
}

// ─────────────────────────────────────────────────────────
// Campaign Card
// ─────────────────────────────────────────────────────────
function CampaignCard({ campaign, claimedIds }) {
  const coverImg = campaign.img
  const hasItems = campaign.items?.length > 0
  const isClaimed = campaign.items?.every((item) => claimedIds.has(item.id))
  const isQuotaFull = campaign.claim_limit_total !== null &&
    campaign.claimed_count >= campaign.claim_limit_total

  return (
    <Link
      href={`/free-campaign/${campaign.id}`}
      className="group block card rounded-2xl overflow-hidden border border-secondary hover:shadow-md transition-all duration-300 hover:-translate-y-1"
    >
      {/* Cover */}
      <div className="relative w-full aspect-[16/9] overflow-hidden page-surface">
        {coverImg ? (
          <>
            <Image
              fill
              loader={imageLoader}
              src={coverImg}
              alt={campaign.name}
              className="object-cover opacity-70 blur-xl scale-110 transition-opacity duration-500 group-hover:opacity-90"
            />
            <Image
              fill
              loader={imageLoader}
              src={coverImg}
              alt={campaign.name}
              className="object-contain relative z-10 p-2"
            />
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center page-surface">
            <svg className="w-16 h-16 text-disable" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.75 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
        )}

        {/* Status overlay */}
        {isQuotaFull && (
          <div className="absolute inset-0 z-20 bg-black/60 flex items-center justify-center">
            <span className="text-white font-bold text-lg tracking-wider">โควตาเต็มแล้ว</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        <h3 className="text-primary font-semibold text-base leading-snug line-clamp-2 transition-colors">
          {campaign.name}
        </h3>

        <div className="flex items-center gap-2 flex-wrap">
          <GrantBadge mode={campaign.grant_mode} days={campaign.access_duration_days} />
          {hasItems && (
            <span className="text-xs text-secondary">{campaign.items.length} รายการ</span>
          )}
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-secondary text-xs">หมดเขต</span>
          <Countdown endAt={campaign.claim_end_at} />
        </div>

        {/* CTA */}
        <div className={`w-full  text-center py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${isClaimed
          ? 'btn-secondary text-success border border-secondary'
          : isQuotaFull
            ? 'btn-secondary text-disable border border-secondary cursor-not-allowed'
            : 'btn-primary text-white shadow-md'
          }`}>
          {isClaimed ? '✓ รับสิทธิ์แล้ว' : isQuotaFull ? 'เต็มแล้ว' : 'รับสิทธิ์อ่านฟรี'}
        </div>
      </div>
    </Link>
  )
}

// ─────────────────────────────────────────────────────────
// My Claims Tab
// ─────────────────────────────────────────────────────────
function MyClaims({ claims }) {
  if (claims.length === 0) {
    return (
      <div className="text-center py-16 text-secondary">
        <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        <p>ยังไม่มีสิทธิ์ที่รับไว้</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {claims.map((claim) => {
        const isExpired = claim.expires_at && dayjs().isAfter(dayjs(claim.expires_at))
        const isRevoked = claim.status === 'revoked'

        return (
          <div
            key={claim.id}
            className={`flex items-center gap-4 p-4 rounded-xl border border-secondary card transition-all ${isRevoked || isExpired ? 'opacity-50' : ''
              }`}
          >
            {/* Book cover */}
            <div className=" h-14 rounded overflow-hidden aspect-[16/9] flex-shrink-0 page-surface">
              {(() => {
                const img = claim.campaignBook?.book?.book_img ||
                  claim.campaignBook?.book?.book_cover ||
                  claim.campaign?.img || null
                return img ? (
                  <Image
                    width={40}
                    height={56}
                    loader={imageLoader}
                    src={img}
                    alt="book"
                    className="object-cover  w-full h-full"
                  />
                ) : <div className="w-full h-full" />
              })()}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <p className="text-primary text-sm font-medium truncate">
                {claim.campaign?.name ?? '—'}
              </p>
              <p className="text-secondary text-xs mt-0.5">
                รับเมื่อ {dayjs(claim.claimed_at).format('DD MMM YYYY')}
              </p>
              <p className="text-xs mt-1">
                {isRevoked ? (
                  <span className="text-danger">ถูก revoke</span>
                ) : isExpired ? (
                  <span className="text-disable">หมดอายุแล้ว</span>
                ) : (
                  <span className="text-success">
                    ถึง {dayjs(claim.expires_at).format('DD MMM YYYY HH:mm')}
                  </span>
                )}
              </p>
            </div>

            {/* Status badge */}
            <div className={`flex-shrink-0 text-xs rounded-full px-2.5 py-1 font-medium ${isRevoked ? 'bg-danger text-white' :
              isExpired ? 'btn-secondary text-disable' :
                'bg-success text-white'
              }`}>
              {isRevoked ? 'Revoked' : isExpired ? 'หมดอายุ' : 'Active'}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────
export default function FreeCampaignList() {
  const { API, headers, privateKey } = useGlobalContext()
  const [campaigns, setCampaigns] = useState([])
  const [claims, setClaims] = useState([])
  const [tab, setTab] = useState('active')
  const [loading, setLoading] = useState(true)
  const [claimedIds, setClaimedIds] = useState(new Set())
  const [toast, setToast] = useState(null)

  const showToast = useCallback((msg, type) => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 3500)
  }, [])

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [campRes, claimRes] = await Promise.all([
        AxiosGetData(API, '/api/get_free_campaigns', headers, privateKey),
        AxiosGetData(API, '/api/checkauthent/my_free_claims', headers, privateKey),
      ])

      const campList = Array.isArray(campRes?.data) ? campRes.data : Array.isArray(campRes) ? campRes : []
      const claimList = Array.isArray(claimRes?.data) ? claimRes.data : Array.isArray(claimRes) ? claimRes : []

      setCampaigns(campList)
      setClaims(claimList)

      const ids = new Set(claimList.map((cl) => cl.free_campaign_book_id))
      setClaimedIds(ids)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [API, headers, privateKey])

  useEffect(() => { loadData() }, [loadData])

  return (
    <div className="min-h-screen text-primary max-w-[1200px] mx-auto px-4 py-6">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-5 right-5 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-medium transition-all ${toast.type === 'success' ? 'bg-success text-white' : 'bg-danger text-white'
          }`}>
          {toast.msg}
        </div>
      )}

      {/* Hero */}
      <div className="relative overflow-hidden page-surface rounded-2xl p-6 md:p-10 mb-8 border border-secondary">
        <div className="flex items-center gap-3 mb-2">
          <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest text-special uppercase">
            <span className="w-2 h-2 rounded-full bg-special animate-pulse" />
            Free Reading
          </span>
        </div>
        <h1 className="text-3xl md:text-4xl font-extrabold text-primary leading-tight">
          อ่านฟรี ไม่มีค่าใช้จ่าย
        </h1>
        <p className="text-secondary mt-2 max-w-xl text-base">
          รับสิทธิ์อ่านหนังสือเลือกสรรฟรี เพียงล็อกอินแล้วกดรับ — ไม่ต้องใช้โค้ด
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 p-1 page-surface rounded-xl w-fit mb-8 border border-secondary">
        {[
          { key: 'active', label: 'แคมเปญที่เปิดอยู่' },
          { key: 'myclaims', label: 'สิทธิ์ของฉัน' },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${tab === t.key
              ? 'btn-primary text-white shadow-md'
              : 'btn-secondary text-secondary hover:text-primary'
              }`}
          >
            {t.label}
            {t.key === 'myclaims' && claims.length > 0 && (
              <span className="ml-1.5 text-xs bg-black/10 dark:bg-white/20 px-1.5 py-0.5 rounded-full">{claims.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-2xl page-surface animate-pulse h-72 border border-secondary" />
          ))}
        </div>
      ) : tab === 'active' ? (
        campaigns.length === 0 ? (
          <div className="text-center py-20 text-secondary">
            <svg className="w-14 h-14 mx-auto mb-4 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.75 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            <p className="text-lg font-medium">ยังไม่มีแคมเปญที่เปิดอยู่</p>
            <p className="text-sm mt-1">กลับมาตรวจสอบใหม่เร็วๆ นี้</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {campaigns.map((c) => (
              <CampaignCard
                key={c.id}
                campaign={c}
                claimedIds={claimedIds}
              />
            ))}
          </div>
        )
      ) : (
        <MyClaims claims={claims} />
      )}
    </div>
  )
}
